// Загрузка стороннего скрипта по требованию (RBXGD-1101).
//
// Все сторонние скрипты жили в <head> и стартовали на 578-й миллисекунде —
// раньше, чем приложение успевало запросить курсы и остатки, то есть отбирали
// полосу у контента страницы. Часть из них при этом не нужна вовсе: SmartCaptcha
// грузилась на главной, где формы нет, а Jivo тянул девять запросов и три mp3
// со звуками чата, который никто не открывал.
//
// Промис кэшируется по адресу: компонент может смонтироваться несколько раз
// (модалка входа открывается и закрывается), а тег нужен один. Кэшируется именно
// промис, а не флаг «загружено» — иначе два одновременных вызова вставили бы два
// тега.
const pending = new Map()

/**
 * @param {string} src адрес скрипта
 * @returns {Promise<void>} резолвится, когда скрипт загружен
 */
export function loadScript(src) {
  if (pending.has(src)) return pending.get(src)

  const promise = new Promise((resolve, reject) => {
    // Тег мог попасть в разметку раньше — например, остаться в index.html.
    const existing = document.querySelector(`script[src="${src}"]`)

    if (existing) {
      if (existing.dataset.loaded === '1') resolve()
      else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error(`не загрузился ${src}`)))
      }
      return
    }

    const el = document.createElement('script')

    el.src = src
    el.async = true
    el.addEventListener('load', () => {
      el.dataset.loaded = '1'
      resolve()
    })
    el.addEventListener('error', () => {
      // Из кэша убираем: неудача может быть сетевой, и повторная попытка имеет
      // смысл. Иначе один сбой навсегда лишил бы пользователя капчи.
      pending.delete(src)
      reject(new Error(`не загрузился ${src}`))
    })

    document.head.appendChild(el)
  })

  pending.set(src, promise)

  return promise
}

/**
 * Ждём появления глобальной переменной, которую объявляет скрипт. Событие load
 * говорит, что файл выполнен, но некоторые библиотеки доопределяют свой API
 * asynchronously; без этой проверки можно поймать undefined сразу после load.
 */
export function waitForGlobal(name, timeout = 5000) {
  if (window[name]) return Promise.resolve(window[name])

  return new Promise((resolve, reject) => {
    const startedAt = Date.now()
    const tick = () => {
      if (window[name]) return resolve(window[name])
      if (Date.now() - startedAt > timeout) return reject(new Error(`не дождались window.${name}`))
      setTimeout(tick, 50)
    }

    tick()
  })
}

/**
 * Отложить работу до момента, когда пользователь что-то сделал, либо до простоя
 * браузера. Нужно для виджетов, без которых первый экран полноценен: чат,
 * счётчики. Слушатели пассивные и одноразовые — они не должны мешать скроллу.
 */
export function onFirstInteraction(callback, fallbackDelay = 10000) {
  let done = false
  const events = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll']

  const run = () => {
    if (done) return
    done = true
    events.forEach(name => window.removeEventListener(name, run))
    callback()
  }

  events.forEach(name => window.addEventListener(name, run, { passive: true, once: true }))

  // Фолбэк нужен: пользователь может читать страницу, ничего не трогая, а чат
  // поддержки всё равно должен появиться.
  //
  // Это именно задержка, а не requestIdleCallback с timeout. Проверено
  // замером: браузер простаивает уже через сотни миллисекунд после загрузки,
  // и requestIdleCallback срабатывал через ~3 секунды сам по себе — то есть
  // «по взаимодействию» превращалось в «на старте с небольшой отсрочкой».
  // Ждём паузу, и только потом отдаём работу в простой.
  setTimeout(() => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 })
    else run()
  }, fallbackDelay)
}
